import { z } from 'zod';

// Mirror of TAG_CODE_REGEX in the API.
const TAG_CODE_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// ────────────────────────────────────────────────────────────────────────────
// Server-shaped row (validated when consumed)
// ────────────────────────────────────────────────────────────────────────────

export const tagSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Tag = z.infer<typeof tagSchema>;

const tagTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  name: z.string(),
});
export const tagDetailSchema = tagSchema.extend({
  translations: z.array(tagTranslationSchema),
});
export type TagDetail = z.infer<typeof tagDetailSchema>;

export const tagOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
export type TagOption = z.infer<typeof tagOptionSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema
// ────────────────────────────────────────────────────────────────────────────

export const tagFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(), // display-only; stripped before submit
  name: z.string().max(200),
});
export type TagFormTranslation = z.infer<typeof tagFormTranslationSchema>;

export const tagFormSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(100, 'Code must be at most 100 characters')
    .regex(TAG_CODE_REGEX, 'Lowercase alphanumerics joined by single hyphens'),
  name: z.string().min(1, 'Name is required').max(200),
  isActive: z.boolean(),
  translations: z.array(tagFormTranslationSchema),
});
export type TagFormValues = z.infer<typeof tagFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

export const TAG_SORT_KEYS = ['code', 'name', 'created', 'updated'] as const;
export type TagSortKey = (typeof TAG_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListTagsParams {
  page: number;
  pageSize: number;
  searchText?: string;
  // 'true'/'false' as string mirrors the API DTO (and market-prices isActive).
  isActive?: 'true' | 'false';
  sortBy?: TagSortKey;
  sortDir?: SortDirection;
}
